class RIconLottie extends HTMLElement {
  static get observedAttributes() {
    return ['tier'];
  }

  constructor() {
    super();

    this.lottiePlayer = null;
    this.picture = null;
    this.source1x = null;
    this.source2x = null;
    this.img = null;

    this.data = {};
    this.dataNode = null;
    this.tier = (this.getAttribute('tier') || '').toLowerCase();
    this.tierData = null;
    this.configLoaded = false;

    this.observer = null;
    this.isVisible = false;
  }

  connectedCallback() {
    if (!this.configLoaded) {
      this.loadConfig();
    }

    this.observeVisibility();

    if (!this.tierData) {
      return;
    }

    this.updateFromTierData();
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  // Defer loading the lottie JSON until the icon is on screen. Hidden icons
  // (e.g. in closed drawers) never intersect, so they never load it.
  observeVisibility() {
    if (this.isVisible || this.observer) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        this.isVisible = true;
        this.observer.disconnect();
        this.observer = null;

        if (this.tierData) {
          this.loadLottie();
        }
      },
      { rootMargin: '200px' }
    );

    this.observer.observe(this);
  }

  loadConfig() {
    this.lottiePlayer = this.querySelector('lottie-player');
    this.picture = this.querySelector('picture');

    if (!this.picture) {
      console.error('No placeholder element found for lottie-player.');
      return;
    }

    const sources = this.picture.querySelectorAll('source');

    this.source1x = sources[0] || null;
    this.source2x = sources[1] || null;
    this.img = this.picture.querySelector('img');

    this.dataNode = document.querySelector('script#r-icon-lottie-data');

    if (!this.lottiePlayer) {
      console.error('No lottie-player element found.');
      return;
    }

    if (!this.dataNode) {
      console.error('No lottie files provided.');
      return;
    }

    try {
      this.data = JSON.parse(this.dataNode.textContent);
    } catch (error) {
      console.error('Failed to parse r-icon-lottie data JSON.', error);
      return;
    }

    this.tierData = this.data[this.tier];

    if (!this.tierData) {
      console.error(`No lottie files found for tier: ${this.tier}`);
      return;
    }

    this.configLoaded = true;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== 'tier' || oldValue === newValue) return;

    this.tier = (newValue || '').toLowerCase();

    if (!this.configLoaded) {
      this.loadConfig();
    } else {
      this.tierData = this.data[this.tier];
    }

    if (!this.tierData) {
      console.error(`No lottie files found for tier: ${this.tier}`);
      return;
    }

    this.updateFromTierData();
  }

  loadLottie() {
    const { lottie } = this.tierData;

    if (this.lottiePlayer.getAttribute('src') === lottie) return;

    const newPlayer = this.lottiePlayer.cloneNode(true);
    newPlayer.setAttribute('src', lottie);
    this.lottiePlayer.replaceWith(newPlayer);
    this.lottiePlayer = newPlayer;
  }

  updateFromTierData() {
    const { '1x_icon': icon1x, '2x_icon': icon2x } = this.tierData;

    if (this.isVisible) {
      this.loadLottie();
    }

    if (this.source1x.getAttribute('srcset') !== icon1x) {
      this.source1x.setAttribute('srcset', icon1x);
    }

    if (this.source2x.getAttribute('srcset') !== icon2x) {
      this.source2x.setAttribute('srcset', icon2x);
    }

    if (this.img.getAttribute('src') !== icon1x) {
      this.img.setAttribute('src', icon1x);
    }
  }
}
customElements.define('r-icon-lottie', RIconLottie);
